import { formatDate } from "helpers/utils.helper"
import { DATE_TIME_FORMAT } from "helpers/dateFormat.helper"

/**
 * `GET /drawings/{id}/processings/` answers with a MAP whose keys are human-readable dates, and the
 * newest one carries a literal `" (latest)"` suffix — the only "newest" signal the payload has.
 *
 * The key is **day-first with a time**: `"08-05-2026 14:03 (latest)"`. Note the OpenAPI example
 * shows a date with NO time, which is stale (see `.claude/context/api/drift.md`) — parsing with a
 * date-only format makes `date-fns` throw and the raw key render instead.
 */
const PROCESSING_RUN_KEY_FORMAT = "dd-MM-yyyy HH:mm"

const LATEST_SUFFIX = " (latest)"

/**
 * Splits a processings map key into its formatted date and whether it is the latest run, so both
 * consumers render the same date while styling the "(latest)" marker their own way.
 *
 * Lives here rather than in either component because two places show these keys — the file-info
 * drawer's "Processing run" selector and the create-quotation modal's source dropdown — and the
 * drawer was left rendering the raw key when the modal was first given a format (#1400).
 *
 * @param key - A raw key from the processings map, with or without the `" (latest)"` suffix
 * @returns The date as MM/DD/YYYY HH:mm, plus whether this key was marked latest
 */
const parseProcessingRunKey = (key: string): { date: string; isLatest: boolean } => ({
	date: formatDate(key.replace(LATEST_SUFFIX, ""), PROCESSING_RUN_KEY_FORMAT, DATE_TIME_FORMAT),
	isLatest: key.includes(LATEST_SUFFIX),
})

/**
 * The whole label as one string, `"<date> (latest)"` for the newest run. For a caller that needs to
 * style the marker separately, use `parseProcessingRunKey` instead.
 */
const formatProcessingRunLabel = (key: string): string => {
	const { date, isLatest } = parseProcessingRunKey(key)
	return isLatest ? `${date}${LATEST_SUFFIX}` : date
}

export { parseProcessingRunKey, formatProcessingRunLabel, PROCESSING_RUN_KEY_FORMAT }
